import React, { useState, useEffect } from 'react';
import DataTable from '../../../Common/DataTable/DataTable';
import DataTableHeader from '../../../Common/DataTableHeader/DataTableHeader';
import DataTableFooter from '../../../Common/DataTableFooter/DataTableFooter';
import api, { RBAC_BASE } from '../../../api/rbacApi';
import { useMemo } from "react";

const PermissionListTable = () => {
  const [dataT, setDataT] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [editData, setEditData] = useState({});
  const [search, setSearch] = useState("");
  const [moduleFilter, setModuleFilter] = useState("ALL");
  const [openModules, setOpenModules] = useState([]);
  const [newPerm, setNewPerm] = useState({
    vchCode: "",
    vchModule: "",
    vchDescription: "",
    bitStatus: 0
  });

  useEffect(() => {
    load();
  }, []);

  const modules = useMemo(() => {
    return [...new Set(dataT.map(p => p.vchModule))];
  }, [dataT]);

  const filteredData = useMemo(() => {
    return dataT.filter(p => {
      const matchesSearch =
        p.vchCode.toLowerCase().includes(search.toLowerCase()) ||
        p.vchDescription?.toLowerCase().includes(search.toLowerCase());

      const matchesModule =
        moduleFilter === "ALL" || p.vchModule === moduleFilter;

      return matchesSearch && matchesModule;
    });
  }, [dataT, search, moduleFilter]);

  useEffect(() => {
    if (moduleFilter !== "ALL") {
      setOpenModules([moduleFilter]);
    }
  }, [moduleFilter]);

  const load = async () => {
    const res = await api.get(`${RBAC_BASE}/permissions`);
    setDataT(res.data);
  };

  const toggleModule = (module) => {
    setOpenModules(prev =>
      prev.includes(module)
        ? prev.filter(m => m !== module)
        : [...prev, module]
    );
  };

  // CREATE
  const handleCreate = async () => {
    if (!newPerm.vchCode) {
      alert("Permission code is required");
      return;
    }

    const exists = dataT.some(
      p => p.vchCode.toLowerCase() === newPerm.vchCode.toLowerCase()
    );

    if (exists) {
      alert("Permission already exists");
      return;
    }

    try {
      await api.post(`${RBAC_BASE}/permissions`, {
        vchCode: newPerm.vchCode,
        vchModule: newPerm.vchModule,
        vchDescription: newPerm.vchDescription,
        bitStatus: newPerm.bitStatus ? 1 : 0
      });

      alert("Permission created successfully");

      setNewPerm({
        vchCode: "",
        vchModule: "",
        vchDescription: "",
        bitStatus: 0
      });

      load();

    } catch (err) {
      console.error(err);

      const msg =
        err.response?.data?.message ||
        "Failed to create permission";

      alert(`${msg}`);
    }
  };

  // EDIT CHANGE
  const handleChange = (id, field, value) => {
    setDataT(prev =>
      prev.map(p =>
        p.intPermissionId === id
          ? { ...p, [field]: value }
          : p
      )
    );
  };

  // UPDATE
  const handleSave = async (id) => {
    try {

      const payload = {
        vchCode: editData.vchCode,
        vchModule: editData.vchModule,
        vchDescription: editData.vchDescription,
        bitStatus: editData.bitStatus
      };

      console.log("Updated data:", payload);

      await api.put(`${RBAC_BASE}/permissions/${id}`, payload);

      alert("Permission updated");

      setEditRow(null);
      setEditData({});
      load();

    } catch (err) {
      console.error(err);
      alert("Update failed");
    }
  };

  // DELETE
  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.vchCode}?`)) return;

    try {
      await api.delete(`${RBAC_BASE}/permissions/${row.intPermissionId}/permanent`);

      alert("Permission deleted");

      load();

    } catch (err) {
      console.error(err);
      alert("Delete failed");
    }
  };

  const columns = useMemo(() => [
    { Header: 'ID', accessor: 'intPermissionId' },

    {
      Header: 'Code',
      Cell: ({ row }) => {
        const d = row.original;
        return editRow === d.intPermissionId ? (
          <input
            className="form-control"
            value={editData.vchCode ?? ""}
            onChange={(e) =>
              setEditData(prev => ({ ...prev, vchCode: e.target.value }))
            }
          />
        ) : d.vchCode;
      }
    },

    {
      Header: 'Module',
      Cell: ({ row }) => {
        const d = row.original;
        return editRow === d.intPermissionId ? (
          <input
            className="form-control"
            value={editData.vchModule ?? ""}
            onChange={(e) =>
              setEditData(prev => ({ ...prev, vchModule: e.target.value }))
            }
          />
        ) : d.vchModule;
      }
    },

    {
      Header: 'Description',
      Cell: ({ row }) => {
        const d = row.original;
        return editRow === d.intPermissionId ? (
          <input
            className="form-control"
            value={editData.vchDescription ?? ""}
            onChange={(e) =>
              setEditData(prev => ({ ...prev, vchDescription: e.target.value }))
            }
          />
        ) : d.vchDescription;
      }
    },

    {
      Header: 'Action',
      Cell: ({ row }) => {
        const d = row.original;
        const isEditing = editRow === d.intPermissionId;

        return isEditing ? (
          <>
            <button
              className="btn btn-sm btn-success me-2"
              onClick={() => handleSave(d.intPermissionId)}
            >
              Save
            </button>

            <button
              className="btn btn-sm btn-light"
              onClick={() => setEditRow(null)}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            <button
              className="btn btn-sm btn-outline-primary me-2"
              onClick={() => {
                setEditRow(d.intPermissionId);
                setEditData({
                  vchCode: d.vchCode,
                  vchModule: d.vchModule,
                  vchDescription: d.vchDescription,
                  bitStatus: d.bitStatus,
                  originalModule: d.vchModule
                });
              }}
            >
              Edit
            </button>

            <button
              className="btn btn-sm btn-outline-danger"
              title="Permanent Delete"
              onClick={() => handleDelete(d)}
            >
              Delete
            </button>
          </>
        );
      }
    },

    {
      Header: 'Status',
      Cell: ({ row }) => {
        const d = row.original;

        return (
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              checked={Boolean(d.bitStatus)}
              onChange={async (e) => {
                const newStatus = e.target.checked;

                handleChange(d.intPermissionId, "bitStatus", newStatus ? 1 : 0);

                try {
                  await api.put(`${RBAC_BASE}/permissions/${d.intPermissionId}`, {
                    vchCode: d.vchCode,
                    vchModule: d.vchModule,
                    vchDescription: d.vchDescription,
                    bitStatus: newStatus ? 1 : 0
                  });

                  alert(`Permission ${newStatus ? "activated" : "deactivated"}`);

                } catch (err) {
                  console.error(err);
                  alert("Status update failed");

                  // rollback
                  handleChange(d.intPermissionId, "bitStatus", newStatus ? 0 : 1);
                }
              }}
            />
          </div>
        );
      }
    }

  ], [editRow, editData]);

  const moduleDataMap = useMemo(() => {
    const map = {};

    filteredData.forEach(p => {
      let key = p.vchModule;

      // freeze grouping for editing row
      if (editRow === p.intPermissionId && editData.originalModule) {
        key = editData.originalModule;
      }

      if (!map[key]) map[key] = [];
      map[key].push(p);
    });

    return map;
  }, [filteredData, editRow, editData]);

  return (
    <>
      {/* CREATE PERMISSION */}
      <div className="card mb-3 border-0 shadow-sm">
        <div className="card-header">
          <h5 className="card-title mb-0">Create New Permission</h5>
        </div>
        <div className="card-body d-flex gap-2 align-items-center">

          <input
            className="form-control"
            placeholder="Code"
            value={newPerm.vchCode}
            onChange={(e) =>
              setNewPerm({ ...newPerm, vchCode: e.target.value })
            }
          />

          <input
            className="form-control"
            placeholder="Module"
            value={newPerm.vchModule}
            onChange={(e) =>
              setNewPerm({ ...newPerm, vchModule: e.target.value })
            }
          />

          <input
            className="form-control"
            placeholder="Description"
            value={newPerm.vchDescription}
            onChange={(e) =>
              setNewPerm({ ...newPerm, vchDescription: e.target.value })
            }
          />

          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              checked={Boolean(newPerm.bitStatus)}
              onChange={(e) =>
                setNewPerm({ ...newPerm, bitStatus: e.target.checked ? 1 : 0 })
              }
            />
          </div>

          <button className="btn btn-primary" onClick={handleCreate}>
            Add
          </button>

        </div>
      </div>

      {/* SEARCH + FILTER */}
      <div className="d-flex gap-2 mb-3 align-items-center">

        <input
          className="form-control"
          placeholder="Search permission..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: "300px" }}
        />

        <select
          className="form-select"
          value={moduleFilter}
          onChange={(e) => setModuleFilter(e.target.value)}
          style={{ maxWidth: "200px" }}
        >
          <option value="ALL">All Modules</option>
          {modules.map(m => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>

      </div>

      {/* <DataTableHeader /> */}

      <div className="mb-2 d-flex gap-2">

        <button
          className="btn btn-sm btn-light"
          onClick={() => setOpenModules(modules)}
        >
          Expand All
        </button>

        <button
          className="btn btn-sm btn-light"
          onClick={() => setOpenModules([])}
        >
          Collapse All
        </button>

      </div>

      <div className="accordion" id="permissionAccordion">

        {modules
          .filter(m => moduleFilter === "ALL" || m === moduleFilter)
          .map((module, index) => {

            const moduleData = moduleDataMap[module] || [];

            if (moduleData.length === 0) return null;

            const isOpen = openModules.includes(module);

            return (
              <div className="accordion-item border-0 mb-3 shadow-sm rounded" key={module}>

                {/* HEADER */}
                <h2 className="accordion-header">
                  <button
                    className={`accordion-button ${!isOpen ? "collapsed" : ""}`}
                    onClick={() => toggleModule(module)}
                    style={{
                      background: "#f8f9fa",
                      fontWeight: "600"
                    }}
                  >
                    <div className="d-flex justify-content-between w-100 align-items-center">

                      <span>{module}</span>

                      <span className="badge bg-light text-dark ms-2">
                        {moduleData.length}
                      </span>

                    </div>
                  </button>
                </h2>

                {/* BODY */}
                <div className={`accordion-collapse collapse ${isOpen ? "show" : ""}`}>
                  <div className="accordion-body p-2">

                    <DataTable columns={columns} data={moduleData} />

                  </div>
                </div>

              </div>
            );
          })}
      </div>

      {/* <DataTableFooter dataT={dataT} /> */}

    </>
  );
};

export default PermissionListTable;